import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Star, Target, TrendingUp, Trophy, type LucideIcon } from 'lucide-react';
import type { DomainAssessment, DomainSavings, OrgSavingsSummary } from '@lizrd/contracts';
import { getDomainHistory, getOrgSavingsSummary, listDomainAssessments } from '../../lib/api';
import { Card } from '../ui/card';
import { Select } from '../ui/select';
import { SkeletonRows } from '../ui/skeleton';
import { UtilizationScore } from '../UtilizationScore';
import { seriesDelta } from '../ui/trend-chart';
import { usd } from '../../lib/graph-visuals';
import { Cost } from '../ui/Cost';
import { recoverableSaving } from '../../lib/savings';
import { useOrgDomainRecs } from './useOrgDomainRecs';

/** One domain's standing, merged from savings + assessment + history. */
interface Row {
  domainId: string;
  name: string;
  estimatedMonthlySaving: number;
  realizedMonthlySaving: number;
  openCount: number;
  validatedCount: number;
  utilizationScore: number | null;
  totalMonthlyCost: number | null;
  billedMonthlyCost: number | null;
  projectedBilledCost: number | null;
  /** First→last change in utilization score over the history window (points). */
  utilDelta: number | null;
}

type RankKey = 'opportunity' | 'realized' | 'utilization' | 'improved';

const RANK_OPTIONS: { value: RankKey; label: ReactNode; text: string }[] = [
  {
    value: 'opportunity',
    label: (
      <span className="flex items-center gap-2">
        <Target aria-hidden className="h-4 w-4" />
        Biggest opportunity
      </span>
    ),
    text: 'Biggest opportunity',
  },
  {
    value: 'realized',
    label: (
      <span className="flex items-center gap-2">
        <Trophy aria-hidden className="h-4 w-4" />
        Most realized
      </span>
    ),
    text: 'Most realized',
  },
  {
    value: 'utilization',
    label: (
      <span className="flex items-center gap-2">
        <Star aria-hidden className="h-4 w-4" />
        Best utilized
      </span>
    ),
    text: 'Best utilized',
  },
  {
    value: 'improved',
    label: (
      <span className="flex items-center gap-2">
        <TrendingUp aria-hidden className="h-4 w-4" />
        Most improved
      </span>
    ),
    text: 'Most improved',
  },
];

/** Sort a copy of the rows by the chosen ranking (nulls always sink). */
function rankBy(rows: Row[], key: RankKey): Row[] {
  const val = (r: Row): number | null => {
    switch (key) {
      case 'opportunity':
        return r.estimatedMonthlySaving;
      case 'realized':
        return r.realizedMonthlySaving;
      case 'utilization':
        return r.utilizationScore;
      case 'improved':
        return r.utilDelta;
    }
  };
  return rows.slice().sort((a, b) => (val(b) ?? -Infinity) - (val(a) ?? -Infinity));
}

interface EarnedBadge {
  Icon: LucideIcon;
  title: string;
}

/**
 * Which merit badges a domain has earned, relative to the whole fleet.
 * "Well optimized" is absolute (a high utilization score); the other three are
 * superlatives — awarded only to the single leader, and only when the metric is
 * actually positive (no "winner" of an empty field). Rendered as compact icon
 * chips (tooltip on hover) so a row with several awards stays tidy.
 */
function badgesFor(row: Row, leaders: Leaders): EarnedBadge[] {
  const out: EarnedBadge[] = [];
  if (row.utilizationScore != null && row.utilizationScore >= 4)
    out.push({ Icon: Star, title: 'Well optimized' });
  if (leaders.topRealized === row.domainId) out.push({ Icon: Trophy, title: 'Top realized' });
  if (leaders.topOpportunity === row.domainId)
    out.push({ Icon: Target, title: 'Biggest opportunity' });
  if (leaders.mostImproved === row.domainId) out.push({ Icon: TrendingUp, title: 'Most improved' });
  return out;
}

interface Leaders {
  topRealized: string | null;
  topOpportunity: string | null;
  mostImproved: string | null;
}

/** The single fleet leader for each superlative (null when no positive value). */
function computeLeaders(rows: Row[]): Leaders {
  const leaderOf = (pick: (r: Row) => number | null): string | null => {
    let best: Row | null = null;
    for (const r of rows) {
      const v = pick(r);
      if (v == null || v <= 0) continue;
      if (!best || v > (pick(best) ?? -Infinity)) best = r;
    }
    return best?.domainId ?? null;
  };
  return {
    topRealized: leaderOf((r) => r.realizedMonthlySaving),
    topOpportunity: leaderOf((r) => r.estimatedMonthlySaving),
    mostImproved: leaderOf((r) => r.utilDelta),
  };
}

/** A ▲/▼ chip for the utilization-score change over the window. */
function DeltaChip({ delta }: { delta: number | null }) {
  if (delta == null || Math.abs(delta) < 0.05) {
    return <span className="text-[0.6rem] text-muted">—</span>;
  }
  const up = delta > 0;
  return (
    <span className={`text-[0.65rem] font-semibold ${up ? 'text-success' : 'text-danger'}`}>
      {up ? '▲' : '▼'} {up ? '+' : ''}
      {delta.toFixed(1)}
    </span>
  );
}

function mergeRows(
  summary: OrgSavingsSummary,
  assessments: DomainAssessment[],
  deltaById: Map<string, number | null>,
  recoverableById: Map<string, number>,
): Row[] {
  const byId = new Map(assessments.map((a) => [a.domainId, a]));
  return summary.domains.map((e: DomainSavings) => {
    const a = byId.get(e.domainId);
    return {
      domainId: e.domainId,
      name: e.name,
      // "Opportunity" = recoverable from the domain's recommendations, the same
      // source the org savings box + "recoverable by category" use, so they always
      // reconcile (falls back to the summary until the recs load).
      estimatedMonthlySaving: recoverableById.get(e.domainId) ?? e.estimatedMonthlySaving,
      realizedMonthlySaving: e.realizedMonthlySaving,
      openCount: e.openCount,
      validatedCount: e.validatedCount,
      utilizationScore: a?.utilizationScore ?? null,
      totalMonthlyCost: a?.totalMonthlyCost ?? null,
      billedMonthlyCost: a?.billedMonthlyCost ?? null,
      projectedBilledCost: a?.projectedBilledCost ?? null,
      utilDelta: deltaById.get(e.domainId) ?? null,
    };
  });
}

/**
 * The organization's domains ranked as a leaderboard (DASH-001/007/009) —
 * the executive, gamified centre of the org dashboard. Merit badges call out the
 * fleet leaders and any well-utilized domain; the "Rank by" control reorders
 * by biggest opportunity, most realized savings, best utilization, or most
 * improved utilization. Each row deep-links into that domain's dashboard.
 */
export function DomainLeaderboard() {
  const [rank, setRank] = useState<RankKey>('opportunity');
  const summary = useQuery({ queryKey: ['org-savings-summary'], queryFn: getOrgSavingsSummary });
  const assessments = useQuery({
    queryKey: ['domain-assessments'],
    queryFn: listDomainAssessments,
    refetchInterval: 60_000,
  });

  const envIds = summary.data?.domains.map((e) => e.domainId) ?? [];
  // Per-domain utilization history powers the "Most improved" ranking and
  // the ▲/▼ delta chip. React Query dedupes and caches these across the app.
  const histories = useQueries({
    queries: envIds.map((id) => ({
      queryKey: ['domain-history', id],
      queryFn: () => getDomainHistory(id),
      staleTime: 60_000,
    })),
  });

  // Recommendations waiting for execution = open or accepted, not yet applied.
  const { envs: recEnvs } = useOrgDomainRecs();
  const waitingById = new Map(
    recEnvs.map((e) => [
      e.domainId,
      e.recs.filter((r) => r.status === 'open' || r.status === 'accepted').length,
    ]),
  );

  // Recoverable per domain from the recs — the single source that keeps the
  // leaderboard's Opportunity in lock-step with the org savings box (item 5).
  const recoverableById = new Map(recEnvs.map((e) => [e.domainId, recoverableSaving(e.recs)]));
  const recoverableKey = [...recoverableById.entries()].map(([k, v]) => `${k}:${v}`).join(',');

  // A stable dependency key that changes whenever any history query resolves,
  // so the merged rows recompute once the "Most improved" deltas are available.
  const historiesKey = histories.map((h) => h.dataUpdatedAt).join(',');
  const rows = useMemo(() => {
    if (!summary.data) return [];
    const deltaById = new Map<string, number | null>(
      envIds.map((id, i) => {
        const points = histories[i]?.data?.utilizationScore ?? [];
        return [id, seriesDelta(points)?.abs ?? null];
      }),
    );
    return mergeRows(summary.data, assessments.data ?? [], deltaById, recoverableById);
    // recoverableKey (a stable string) stands in for the per-render recoverableById Map.
  }, [summary.data, assessments.data, envIds, histories, historiesKey, recoverableKey]);

  const leaders = useMemo(() => computeLeaders(rows), [rows]);
  const ranked = useMemo(() => rankBy(rows, rank), [rows, rank]);

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-hairline p-4">
        <div>
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
            Domains
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            How every domain is doing — ranked and celebrated.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[0.7rem] uppercase tracking-wide text-muted">Rank by</span>
          <Select
            value={rank}
            onChange={setRank}
            options={RANK_OPTIONS}
            size="sm"
            ariaLabel="Rank domains by"
          />
        </div>
      </div>

      {summary.isLoading ? (
        <SkeletonRows rows={3} rightBlocks={3} />
      ) : ranked.length === 0 ? (
        <p className="p-5 text-sm text-muted">
          No domains yet.{' '}
          <Link to="/domains" className="text-iris hover:text-iris-strong">
            Create your first domain
          </Link>{' '}
          to start finding savings.
        </p>
      ) : (
        <ul className="divide-y divide-hairline">
          {ranked.map((row, i) => {
            const badges = badgesFor(row, leaders);
            return (
              <li key={row.domainId}>
                <Link
                  to={`/domains/${row.domainId}`}
                  className="flex flex-wrap items-center gap-x-6 gap-y-3 p-4 transition-colors hover:bg-surface-2/40"
                >
                  {/* Rank + identity */}
                  <div className="flex min-w-[200px] flex-1 items-center gap-3">
                    <span className="w-5 shrink-0 text-center font-display text-sm font-bold text-muted tnum">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate font-medium text-bone">{row.name}</span>
                        {badges.length > 0 && (
                          <span className="flex flex-none items-center gap-1">
                            {badges.map((b) => (
                              <span
                                key={b.title}
                                title={b.title}
                                aria-label={b.title}
                                className="flex h-5 w-5 items-center justify-center rounded-full border border-hairline bg-surface-2 text-xs leading-none"
                              >
                                <b.Icon aria-hidden className="h-3 w-3" />
                              </span>
                            ))}
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 flex flex-wrap items-center gap-x-1 text-xs text-muted">
                        {row.billedMonthlyCost != null || row.totalMonthlyCost != null ? (
                          <>
                            <Cost
                              value={row.billedMonthlyCost ?? row.totalMonthlyCost}
                              provenance={row.billedMonthlyCost != null ? 'billed' : 'estimated'}
                              projected={row.projectedBilledCost}
                              monthly
                            />
                            <span>spend</span>
                          </>
                        ) : (
                          '—'
                        )}
                        {(waitingById.get(row.domainId) ?? 0) > 0 &&
                          ` · ${waitingById.get(row.domainId)} cost recommendation${
                            waitingById.get(row.domainId) === 1 ? '' : 's'
                          } waiting for execution`}
                      </p>
                    </div>
                  </div>

                  {/* Figures */}
                  <div className="flex items-center gap-6">
                    <div className="min-w-[92px] text-center">
                      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                        Utilization
                      </div>
                      <div className="mt-0.5 flex items-center justify-center gap-1.5">
                        <UtilizationScore score={row.utilizationScore} />
                        <DeltaChip delta={row.utilDelta} />
                      </div>
                    </div>
                    <div className="min-w-[92px] text-center">
                      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                        Opportunity
                      </div>
                      <div className="mt-0.5 text-sm font-medium text-bone tnum">
                        {usd(row.estimatedMonthlySaving)}
                      </div>
                    </div>
                    <div className="min-w-[92px] text-center">
                      <div className="text-[0.6rem] font-semibold uppercase tracking-wide text-muted">
                        Realized
                      </div>
                      <div
                        className={`mt-0.5 text-sm font-medium tnum ${
                          row.realizedMonthlySaving > 0 ? 'text-success' : 'text-muted'
                        }`}
                      >
                        {usd(row.realizedMonthlySaving)}
                      </div>
                    </div>
                    <span className="text-muted" aria-hidden>
                      →
                    </span>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
